import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { MessageCircle, X, Send, Sparkles, Bot } from 'lucide-react';
import { RootState } from '../../support/store';
import { useChatStream } from '../../hooks/useChatStream';
import StbMarkdown from '../security-toolbox-ui/StbMarkdown';

export type AgentClickPayload = {
  kind: 'question' | 'listenFor' | 'point' | 'challenge';
  text: string;
  detail?: string;
  sectionLabel?: string;
};

export type AgentContext = AgentClickPayload & { nonce: number };

type UiMessage = {
  role: 'user' | 'assistant';
  content: string;
};

interface AiAgentChatProps {
  trigger: AgentContext | null;
}

const BREVITY_HINT =
  'Keep the reply conversational and short (2-4 sentences max) — no long paragraphs, headers, or bullet lists unless I explicitly ask for one.';

const where = (ctx: AgentClickPayload) => (ctx.sectionLabel ? ` from "${ctx.sectionLabel}"` : '');

const buildClarifyQuestion = (ctx: AgentClickPayload) => {
  if (ctx.kind === 'question') {
    return `Want a preview of how the client is likely to answer "${ctx.text}", or should I focus on something specific — like the architecture, the stack, or how to position Cisco? Let me know what's most useful.`;
  }
  return `What would you like to know about "${ctx.text}"?`;
};

const buildFollowUpPrompt = (ctx: AgentClickPayload, focus: string) => {
  const base = `An SE is preparing for this${where(ctx)}: "${ctx.text}"${ctx.detail ? ` — ${ctx.detail}` : ''}.`;
  if (ctx.kind === 'question') {
    return `${base} They want: ${focus}. If that's about the client's likely answer, describe realistic example responses to expect from a customer. ${BREVITY_HINT}`;
  }
  return `${base} Specifically, they want to know: ${focus}. ${BREVITY_HINT}`;
};

const buildRolePlayPrompt = (ctx: AgentClickPayload) => {
  return `Role-play as a customer who just raised this${where(ctx)}: "${ctx.text}"${
    ctx.detail ? ` (${ctx.detail})` : ''
  }. Speak in first person as the customer in 1-3 sentences, then stop and wait for the SE's reply. ${BREVITY_HINT}`;
};

const buildRoleplayContinuePrompt = (text: string) => {
  return `(Stay in character as the same customer from this role-play — reply in 1-3 sentences, then stop and wait for my next reply.) ${text}`;
};

export default function AiAgentChat({ trigger }: AiAgentChatProps) {
  const [open, setOpen] = useState(false);
  const [roleplay, setRoleplay] = useState(false);
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [input, setInput] = useState('');
  const [pendingClarify, setPendingClarify] = useState<AgentClickPayload | null>(null);
  const sessionIdRef = useRef(uuidv4());
  const listRef = useRef<HTMLDivElement | null>(null);
  const lastNonceRef = useRef<number | null>(null);

  const userEmail = useSelector((state: RootState) => state.user.userData?.email);
  const userSfId = useSelector((state: RootState) => state.user.userData?.sfId);
  const { send, sending, streamStatus } = useChatStream();

  const appendAssistant = (content: string) => {
    setMessages((prev) => [...prev, { role: 'assistant', content }]);
  };

  const sendToAgent = (backendPrompt: string) => {
    send({
      payload: {
        session_id: sessionIdRef.current,
        message: backendPrompt,
        user_id: userSfId || userEmail || undefined,
        related_type: 'gtm_solution',
        related_id: '10',
        mcp: { enabled: false },
      },
      onAssistantToken: (token) => {
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.role === 'assistant') {
            return [...prev.slice(0, -1), { ...last, content: `${last.content}${token}` }];
          }
          return [...prev, { role: 'assistant', content: token }];
        });
      },
    }).catch((err) => {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `Sorry, something went wrong reaching the agent: ${err?.message || err}` },
      ]);
    });
  };

  useEffect(() => {
    if (!trigger || trigger.nonce === lastNonceRef.current) return;
    lastNonceRef.current = trigger.nonce;
    setOpen(true);
    if (roleplay) {
      setPendingClarify(null);
      sendToAgent(buildRolePlayPrompt(trigger));
    } else {
      appendAssistant(buildClarifyQuestion(trigger));
      setPendingClarify(trigger);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, roleplay]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: text }]);

    if (pendingClarify) {
      const ctx = pendingClarify;
      setPendingClarify(null);
      sendToAgent(roleplay ? buildRolePlayPrompt(ctx) : buildFollowUpPrompt(ctx, text));
    } else if (roleplay) {
      sendToAgent(buildRoleplayContinuePrompt(text));
    } else {
      sendToAgent(`${text}\n\n${BREVITY_HINT}`);
    }
  };

  const placeholder = pendingClarify
    ? "Tell me what you'd like to focus on…"
    : roleplay
    ? 'Reply to the customer…'
    : 'Ask the agent anything…';

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open AI Agent chat"
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#049fd9] text-white shadow-[0_8px_24px_rgba(4,159,217,0.45)] flex items-center justify-center hover:bg-[#0380b0] transition-colors"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[92vw] max-w-sm h-[70vh] max-h-[640px] bg-[#0f2035] border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-white/10 shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-[#049fd9]/15 flex items-center justify-center shrink-0">
                <Bot className="w-4 h-4 text-[#049fd9]" />
              </div>
              <div className="min-w-0">
                <div className="text-white text-sm font-semibold truncate">AI Agent</div>
                {streamStatus && <div className="text-[#6b8299] text-[11px] truncate">{streamStatus}</div>}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close AI Agent chat"
              className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors shrink-0"
            >
              <X className="w-4 h-4 text-[#8eafc8]" />
            </button>
          </div>

          <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-white/10 shrink-0">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-[#8eafc8]">
              <Sparkles className="w-3.5 h-3.5" />
              Role Play
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={roleplay}
              aria-label="Toggle role play mode"
              onClick={() => setRoleplay((v) => !v)}
              className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                roleplay ? 'bg-[#049fd9]' : 'bg-white/15'
              }`}
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                  roleplay ? 'translate-x-[18px]' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>

          <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.length === 0 && (
              <div className="text-[#6b8299] text-xs leading-relaxed text-center py-6">
                Click any question, point, or challenge on the page — I&apos;ll ask what you&apos;d like to
                focus on, then get you an answer. Flip <span className="text-[#8eafc8] font-medium">Role Play</span> to
                have me act as the customer instead, so you can practice your response.
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm ${
                    m.role === 'user'
                      ? 'bg-[#049fd9] text-white'
                      : 'bg-[#0b1929] border border-white/10 text-[#c9d8e6]'
                  }`}
                >
                  {m.role === 'assistant' ? (
                    <div className="prose-invert text-sm [&_p]:my-1.5 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0">
                      <StbMarkdown>{m.content}</StbMarkdown>
                    </div>
                  ) : (
                    <span className="whitespace-pre-wrap">{m.content}</span>
                  )}
                </div>
              </div>
            ))}
            {sending && messages[messages.length - 1]?.role !== 'assistant' && (
              <div className="flex justify-start">
                <div className="bg-[#0b1929] border border-white/10 text-[#6b8299] rounded-xl px-3.5 py-2.5 text-sm">
                  Thinking…
                </div>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="flex items-center gap-2 px-3 py-3 border-t border-white/10 shrink-0">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={placeholder}
              className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-[#4a5d70] focus:outline-none focus:border-[#049fd9]/50"
            />
            <button
              type="submit"
              disabled={!input.trim() || sending}
              aria-label="Send"
              className="w-9 h-9 rounded-lg bg-[#049fd9] text-white flex items-center justify-center disabled:opacity-30 hover:bg-[#0380b0] transition-colors shrink-0"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}

